<script>
  import { extent, scaleLinear } from 'd3'
  import cloud from 'd3-cloud'

  let {
    words = [{ text: 'Word', size: 100, color: '#ccc' }],
    width = 1000,
    height = 600,
    mode = 'cloud',
    activeCategory = null,
    activeWords = null,
    activeWordColors = null,
    showCircles = false,
    circleWords = [],
    fadeToCircles = false,
    offset = 0,
    index = 0,
    entryIndex = 0,
  } = $props()

  const highlightColor = 'var(--project-highlight-color, #cfff5e)'
  const mutedColor = 'rgba(255, 255, 255, 0.2)'
  const highlightScale = $derived(width < 768 ? 2.3 : 1.8) // zoom level index for the highlighted words
  const groupHighlightLH = 1.3 // lineheight between the highlighted group words

  function parseColorToRgba(colorStr) {
    if (!colorStr) return [255, 255, 255, 1]
    if (colorStr.includes('var(')) {
      const match = colorStr.match(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/)
      if (match) colorStr = match[0]
      else return [207, 255, 94, 1] // #cfff5e
    }
    if (colorStr.startsWith('#')) {
      let hex = colorStr.replace('#', '')
      if (hex.length === 3)
        hex = hex
          .split('')
          .map((c) => c + c)
          .join('')
      return [
        parseInt(hex.substring(0, 2), 16) || 255,
        parseInt(hex.substring(2, 4), 16) || 255,
        parseInt(hex.substring(4, 6), 16) || 255,
        1,
      ]
    }
    if (colorStr.startsWith('rgba') || colorStr.startsWith('rgb')) {
      const parts = colorStr.match(/[\d.]+/g)
      if (parts && parts.length >= 3) {
        return [
          parseInt(parts[0], 10),
          parseInt(parts[1], 10),
          parseInt(parts[2], 10),
          parts.length > 3 ? parseFloat(parts[3]) : 1,
        ]
      }
    }
    return [255, 255, 255, 1]
  }

  // Parallax Configuration
  const parallaxSettleThreshold = 0.4 // Offset at which words settle into final position (0 to 1)
  const parallaxYStart = 0.3 // Starting Y offset multiplier based on screen height
  const parallaxXStart = 0.6 // Starting X offset multiplier based on word X position
  const parallaxScaleStart = 0.8 // Starting scale offset multiplier
  const parallaxOpacityStart = 0.03 // Starting opacity (0 to 1)

  const highlightScaleOnScroll = $derived(offset >= 0.3 ? highlightScale : 1)

  let computedWords = $state([])
  let yearColumns = $state([])
  const currentWidth = $derived(mode === 'cloud' ? width : Math.min(width, 1200))

  // Groups order
  const categoryOrder = [
    'Water & Breezes',
    'Greenery & Gardens',
    'Flowers & Seasons',
    'Heights & Skyline',
    'Place & Architecture',
    'Gems & Light',
    'Feeling',
    'Others',
    'Geography & Landscape',
  ]

  const computeGroupsLayout = (renderedWords, currentWidth, height) => {
    const groups = {}
    renderedWords.forEach((w) => {
      const cat = w.category || 'Other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(w)
    })

    const sortedCategories = Object.keys(groups).sort((a, b) => {
      let indexA = categoryOrder.indexOf(a)
      let indexB = categoryOrder.indexOf(b)
      if (indexA === -1) indexA = 999
      if (indexB === -1) indexB = 999

      if (indexA !== indexB) return indexA - indexB
      return groups[b].length - groups[a].length
    })

    const numCols = sortedCategories.length
    if (numCols > 0) {
      const colWidth = currentWidth / numCols
      const startX = -(currentWidth / 2) + colWidth * 0.1
      const startY = -(height / 2) + 60

      const groupFontSize = Math.max(9, Math.min(18, currentWidth / 50))

      let maxWordsInCol = 0
      Object.keys(groups).forEach((cat) => {
        if (groups[cat].length > maxWordsInCol) maxWordsInCol = groups[cat].length
      })
      const availableHeight = height - 120
      const fitLineHeight =
        maxWordsInCol > 0 ? availableHeight / maxWordsInCol : groupFontSize * 1.5
      const lineHeight = Math.min(groupFontSize * 2.5, fitLineHeight)

      sortedCategories.forEach((cat, colIndex) => {
        const colWords = groups[cat].sort((a, b) => a.text.localeCompare(b.text))
        colWords.forEach((w, rowIndex) => {
          w.groupX = startX + colIndex * colWidth
          w.groupY = startY + rowIndex * lineHeight
          w.groupSize = groupFontSize
          w.groupLineHeight = lineHeight
        })
      })
    }
  }

  const computeYearsLayout = (renderedWords, currentWidth, height) => {
    const expandedWords = []
    renderedWords.forEach((w) => {
      const bins = Array.from(w.yearBins || []).sort()
      const primaryBin = bins[0]

      if (bins.length === 0) {
        expandedWords.push({ ...w, id: w.text, isPrimary: true, yearBin: null })
        return
      }

      bins.forEach((bin) => {
        expandedWords.push({
          ...w,
          id: `${w.text}-${bin}`,
          isPrimary: bin === primaryBin,
          yearBin: bin,
        })
      })
    })

    const yearGroups = {}
    expandedWords.forEach((w) => {
      if (w.yearBin) {
        if (!yearGroups[w.yearBin]) yearGroups[w.yearBin] = []
        yearGroups[w.yearBin].push(w)
      }
    })

    const sortedYearBins = Object.keys(yearGroups).sort()
    const numYearCols = sortedYearBins.length
    const newYearCols = []

    if (numYearCols > 0) {
      const colWidth = currentWidth / numYearCols
      const startX = -(currentWidth / 2) + colWidth * 0.1
      const startY = -(height / 2) + 60

      const yearFontSize = Math.max(8, Math.min(14, currentWidth / 75))

      let maxWordsInCol = 0
      Object.keys(yearGroups).forEach((bin) => {
        if (yearGroups[bin].length > maxWordsInCol) maxWordsInCol = yearGroups[bin].length
      })
      const availableHeight = height - 120 // startY is height/2 + 60, so bottom is height/2 - 60 => height - 120 available
      const fitLineHeight = maxWordsInCol > 0 ? availableHeight / maxWordsInCol : yearFontSize * 1.5
      const lineHeight = Math.min(yearFontSize * 2.5, fitLineHeight)

      sortedYearBins.forEach((bin, colIndex) => {
        newYearCols.push({
          label: bin,
          x: startX + colIndex * colWidth,
          y: startY - 40,
        })

        const colWords = yearGroups[bin].sort((a, b) => a.text.localeCompare(b.text))
        colWords.forEach((w, rowIndex) => {
          w.yearX = startX + colIndex * colWidth
          w.yearY = startY + rowIndex * lineHeight
          w.yearSize = yearFontSize
          w.yearLineHeight = lineHeight
        })
      })
    }

    return { expandedWords, newYearCols }
  }

  // d3-cloud gives every word a random start position, so the circled words land
  // somewhere different on each refresh. Override just their final positions to a
  // stable ring near the center so they're always clustered there; when they become
  // active they only scale up in place (no travel to the center). Every other word
  // keeps its d3-cloud position untouched.
  const positionCircleWords = (renderedWords) => {
    if (!circleWords || circleWords.length === 0) return
    const set = new Set(circleWords)
    const cluster = renderedWords.filter((w) => set.has(w.text))
    const n = cluster.length
    if (n === 0) return
    if (n === 1) {
      cluster[0].x = 0
      cluster[0].y = 0
      return
    }
    // Space the ring so the (scaled-up) circles never overlap each other.
    const radiusOf = (w) => w.size * w.text.length * 0.35 * 1.35 + 12
    const maxR = Math.max(...cluster.map(radiusOf))
    const ring = (maxR + 8) / Math.sin(Math.PI / n)
    cluster.forEach((w, i) => {
      const angle = -Math.PI / 2 + (i / n) * Math.PI * 2
      w.x = Math.cos(angle) * ring
      w.y = Math.sin(angle) * ring
    })
  }

  // Debounce layout updates to prevent heavy recalculations on every pixel resize
  let resizeTimer
  let lastLayoutWidth = 0
  let lastLayoutHeight = 0

  $effect(() => {
    // used here instead of derived due to heavy calculations in the d3-cloud library that cause cascading updates
    // so, keep it here for better performance
    const currentWords = words

    if (!currentWidth || !height || !currentWords || currentWords.length === 0) return

    // Ignore tiny width changes (like scrollbar toggling) to prevent columns from shifting left/right
    // Increased height tolerance to prevent mobile address bar from scrambling the layout
    if (
      Math.abs(currentWidth - lastLayoutWidth) < 25 &&
      Math.abs(height - lastLayoutHeight) < 150 &&
      computedWords.length > 0
    ) {
      return
    }

    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      lastLayoutWidth = currentWidth
      lastLayoutHeight = height
      // Find the min/max size of the incoming words to map them properly
      const ext = extent(currentWords, (d) => d.size)
      const sizeScale = scaleLinear()
        .domain(ext[0] === ext[1] ? [0, ext[0]] : ext)
        .range([12, Math.min(currentWidth, height) / 5]) // Map sizes dynamically based on screen size

      // Simple deterministic pseudo-random number generator
      let seed = 12345
      const seededRandom = () => {
        const x = Math.sin(seed++) * 10000
        return x - Math.floor(x)
      }

      const mappedWords = currentWords.map((d) => ({
        text: d.text,
        size: sizeScale(d.size),
        color: d.color || mutedColor, // Use specific color or fallback to muted
        category: d.category,
        yearBins: d.yearBins,
      }))

      const circleSet = new Set(circleWords)

      const layout = cloud()
        .size([currentWidth, height])
        .words(mappedWords)
        .padding(15)
        .rotate(0) // No rotation based on the mockup
        .font('"Selane Twenty", "SelaneTwenty", serif') // Match canvas font-family exactly for accurate collision detection
        .fontSize((d) => d.size)
        .random(seededRandom) // Ensure deterministic layout
        .spiral('rectangular')
        .timeInterval(10)
        .on('end', (renderedWords) => {
          // d3-cloud drops any word its spiral can't fit, which sometimes loses a
          // circle word. Since we pin circle words to the center ring ourselves,
          // re-add any that were dropped so all of them always render.
          const placed = new Set(renderedWords)
          const droppedWords = mappedWords.filter((w) => !placed.has(w))
          droppedWords.forEach((w) => {
            w.x = 0
            w.y = 0
            w.isDropped = true
          })
          const allWords = [...renderedWords, ...droppedWords]

          positionCircleWords(allWords)

          // Always calculate columns layout based on its target width,
          // so it doesn't use the wider cloud width before recalculating
          const columnsWidth = Math.min(width, 1200)

          computeGroupsLayout(allWords, columnsWidth, height)
          const { expandedWords, newYearCols } = computeYearsLayout(allWords, columnsWidth, height)

          if (newYearCols.length > 0) {
            yearColumns = newYearCols
          }
          computedWords = expandedWords
        })

      layout.start()
    }, 200)
  })

  const styledWords = $derived.by(() => {
    if (!computedWords || computedWords.length === 0) return []

    const wordsCopy = computedWords.map((w) => ({ ...w }))

    const activeWordsList = Array.isArray(activeWords) ? activeWords : null
    const activeWordColorMap = activeWordColors || {}

    wordsCopy.forEach((w) => {
      let isHighlighted = false
      if (activeWordsList !== null) {
        if (activeWordsList.length === 0) {
          isHighlighted = false
        } else {
          isHighlighted = activeWordsList.includes(w.text)
        }
      } else if (activeCategory) {
        isHighlighted = activeCategory === w.category
      } else {
        isHighlighted = mode === 'cloud'
      }

      w.isHighlighted = isHighlighted

      let fill = w.color
      if (index === 0) {
        fill = w.color && w.color.includes('highlight-color') ? 'rgba(255, 255, 255, 0.3)' : w.color // last scrolly color highlights here
      } else if (activeWordsList !== null) {
        if (activeWordsList.length > 0) {
          fill = activeWordsList.includes(w.text)
            ? activeWordColorMap[w.text] || highlightColor
            : mutedColor
        } else {
          fill = mutedColor
        }
      } else if (activeCategory) {
        fill = activeCategory === w.category ? highlightColor : mutedColor
      } else {
        fill = w.color
      }
      w.currentFill = fill

      const isCircleWord = circleWords && circleWords.includes(w.text)
      w.currentOpacity = fadeToCircles
        ? 0
        : mode === 'cloud' && w.isDropped && !isCircleWord
          ? 0
          : mode === 'years' || w.isPrimary
            ? 1
            : 0
      w.currentPointerEvents = mode === 'years' || w.isPrimary ? 'auto' : 'none'
      // Keep text-anchor start for all modes to prevent horizontal teleporting, and we'll adjust the cloud X coordinate instead
      w.currentTextAnchor = 'start'
      w.currentRotate = 0

      // Deterministic depth for 3D parallax effect
      let hash = 0
      for (let i = 0; i < w.text.length; i++) {
        hash = w.text.charCodeAt(i) + ((hash << 5) - hash)
      }
      w.depth = 0.4 + (Math.abs(hash) % 120) / 100 // Range: 0.4 to 1.6
    })

    if (mode === 'groups') {
      const groups = {}
      wordsCopy.forEach((w) => {
        const cat = w.category || 'Other'
        if (!groups[cat]) groups[cat] = []
        groups[cat].push(w)
      })

      Object.keys(groups).forEach((cat) => {
        const colWords = groups[cat].sort((a, b) => a.text.localeCompare(b.text))
        let currentY = colWords[0] ? colWords[0].groupY : 0

        const uniqueWordsMap = {}

        colWords.forEach((w) => {
          if (!uniqueWordsMap[w.text]) {
            const scale = w.isHighlighted ? highlightScaleOnScroll : 1
            const originalLineHeight = w.groupLineHeight || w.groupSize * groupHighlightLH
            const currentLineHeight = originalLineHeight * scale

            uniqueWordsMap[w.text] = {
              y: currentY + (currentLineHeight - originalLineHeight) * 0.75,
              fontSize: w.groupSize * scale,
            }
            currentY += currentLineHeight
          }

          w.centerX = w.groupX
          w.centerY = uniqueWordsMap[w.text].y
          w.offsetX = 0
          w.offsetY = 0

          w.currentX = w.centerX + w.offsetX
          w.currentY = w.centerY + w.offsetY
          w.currentFontSize = fadeToCircles ? 0 : uniqueWordsMap[w.text].fontSize
        })
      })
    } else if (mode === 'years') {
      const yearGroups = {}
      wordsCopy.forEach((w) => {
        if (w.yearBin) {
          if (!yearGroups[w.yearBin]) yearGroups[w.yearBin] = []
          yearGroups[w.yearBin].push(w)
        }
      })

      Object.keys(yearGroups).forEach((bin) => {
        const colWords = yearGroups[bin].sort((a, b) => a.text.localeCompare(b.text))
        let currentY = colWords[0] ? colWords[0].yearY : 0

        colWords.forEach((w) => {
          const scale = w.isHighlighted ? highlightScaleOnScroll : 1
          const originalLineHeight = w.isHighlighted
            ? Math.max(w.yearSize * 1.5, w.yearLineHeight || 0)
            : w.yearLineHeight || w.yearSize * 0.9
          const currentLineHeight = originalLineHeight * scale

          w.centerX = w.yearX
          w.centerY = currentY + (currentLineHeight - originalLineHeight) * 0.75
          w.offsetX = 0
          w.offsetY = 0

          w.currentX = w.centerX + w.offsetX
          w.currentY = w.centerY + w.offsetY
          w.currentFontSize = fadeToCircles ? 0 : w.yearSize * scale

          currentY += currentLineHeight
        })
      })
    } else {
      wordsCopy.forEach((w) => {
        let parallaxX = 0
        let parallaxY = 0
        let parallaxScale = 1

        // 3D Parallax entry effect when the scrolly just enters the screen
        if (index <= entryIndex) {
          let remaining = 1
          let opacityMultiplier = 0

          if (index === entryIndex) {
            // Word cloud settles at its final position when offset reaches parallaxSettleThreshold
            const entryProgress = Math.min(1, Math.max(0, offset / parallaxSettleThreshold))
            remaining = 1 - entryProgress

            // Smooth fade in from initial opacity
            const fadeProgress = Math.min(1, Math.max(0, (offset - 0.05) / 0.25))
            opacityMultiplier = parallaxOpacityStart + (1 - parallaxOpacityStart) * fadeProgress
          }

          // Foreground words (depth > 1) start much lower, wider, and larger.
          // Background words (depth < 1) start slightly lower, narrower, and smaller.
          parallaxY = remaining * (height * parallaxYStart) * w.depth
          parallaxX = remaining * w.x * (w.depth - 1) * parallaxXStart
          parallaxScale = 1 + remaining * (w.depth - 1) * parallaxScaleStart

          w.currentOpacity *= opacityMultiplier
        }

        w.centerX = w.x + parallaxX
        w.centerY = w.y + parallaxY
        w.offsetX = w.x0 || 0
        w.offsetY = 0 // d3-cloud y is already the alphabetic baseline

        w.currentX = w.centerX + w.offsetX
        w.currentY = w.centerY + w.offsetY
        w.currentFontSize = fadeToCircles ? 0 : Math.max(0, w.size * parallaxScale)
      })
    }

    if (typeof window !== 'undefined') {
      window.__wordPositions = window.__wordPositions || new Map()
      wordsCopy.forEach((w) => {
        window.__wordPositions.set(w.text, { x: w.currentX, y: w.currentY })
      })
    }

    return wordsCopy
  })

  // Circled words are hoisted to the front of the words array (for d3-cloud
  // placement), which paints them first/behind. Re-order so they render on top.
  const renderWords = $derived.by(() => {
    if (!showCircles) return styledWords
    const circled = (w) => (w.isHighlighted && w.currentOpacity > 0 ? 1 : 0)
    return [...styledWords].sort((a, b) => circled(a) - circled(b))
  })

  const maxHighlightedY = $derived.by(() => {
    let max = 0
    if (mode === 'groups') {
      styledWords.forEach((w) => {
        if (w.isHighlighted) {
          const bottom = w.currentY + w.currentFontSize * 1.5
          if (bottom > max) max = bottom
        }
      })
    }
    return max
  })

  const translateY = $derived.by(() => {
    const threshold = 0.5
    if (mode !== 'groups' || offset < threshold) return 0

    const screenBottom = height / 2 - 200 // 200px padding at bottom
    const overflow = maxHighlightedY - screenBottom

    if (overflow <= 0) return 0

    const progress = (offset - threshold) / (1 - threshold) // Maps 0.3 -> 1.0 to 0.0 -> 1.0
    return -(overflow * progress)
  })

  let isJump = $state(false)
  let previousOffset = offset
  let previousIndex = index
  let isZoomed = $state(offset >= 0.3)

  $effect.pre(() => {
    const newlyZoomed = offset >= 0.3
    isJump =
      Math.abs(offset - previousOffset) > 0.5 || index !== previousIndex || newlyZoomed !== isZoomed
    isZoomed = newlyZoomed
    previousOffset = offset
    previousIndex = index
  })

  let canvasRef = $state(null)
  let rafId = null

  // We maintain a persistent copy of the display state for the canvas
  let canvasWords = []

  // Initialize or update the persistent canvas words when styledWords changes
  $effect(() => {
    if (!styledWords || styledWords.length === 0) return

    if (canvasWords.length === 0) {
      canvasWords = styledWords.map((w) => {
        const isLine = mode !== 'cloud' && !w.isHighlighted
        const rgba = parseColorToRgba(w.currentFill)
        return {
          ...w,
          drawCenterX: w.centerX,
          drawCenterY: w.centerY + (w.isHighlighted ? translateY : 0),
          drawOffsetX: w.offsetX,
          drawOffsetY: w.offsetY,
          drawX: w.currentX,
          drawY: w.currentY + (w.isHighlighted ? translateY : 0),
          drawSize: Math.max(0, w.currentFontSize * (showCircles && w.isHighlighted ? 1.35 : 1)),
          drawTextOpacity: isLine ? 0 : w.currentOpacity,
          drawRectOpacity: isLine ? w.currentOpacity : 0,
          drawCircleR: 0,
          drawCircleOpacity: 0,
          vCenterX: 0,
          vCenterY: 0,
          vOffsetX: 0,
          vOffsetY: 0,
          vx: 0,
          vy: 0,
          vSize: 0,
          vTextOpacity: 0,
          vRectOpacity: 0,
          vCircleR: 0,
          vCircleOpacity: 0,
          drawColorR: rgba[0],
          drawColorG: rgba[1],
          drawColorB: rgba[2],
          drawColorA: rgba[3],
          targetColorR: rgba[0],
          targetColorG: rgba[1],
          targetColorB: rgba[2],
          targetColorA: rgba[3],
          vColorR: 0,
          vColorG: 0,
          vColorB: 0,
          vColorA: 0,
          lastMode: mode,
          lastIndex: index,
          isTransitioning: false,
        }
      })
    } else {
      // Sync targets
      const wordMap = new Map(styledWords.map((w) => [w.id, w]))
      canvasWords.forEach((cw) => {
        const target = wordMap.get(cw.id)
        if (target) {
          cw.isHighlighted = target.isHighlighted
          const isLine = mode !== 'cloud' && !target.isHighlighted

          cw.targetCenterX = target.centerX
          cw.targetCenterY = target.centerY + (target.isHighlighted ? translateY : 0)
          cw.targetOffsetX = target.offsetX
          cw.targetOffsetY = target.offsetY

          cw.targetSize = Math.max(
            0,
            target.currentFontSize * (showCircles && target.isHighlighted ? 1.35 : 1),
          )

          const scaleRatio = target.size > 0 ? cw.targetSize / target.size : 1
          cw.targetX = cw.targetCenterX + cw.targetOffsetX * scaleRatio
          cw.targetY = cw.targetCenterY + cw.targetOffsetY * scaleRatio

          cw.targetTextOpacity = isLine ? 0 : target.currentOpacity
          cw.targetRectOpacity = isLine ? target.currentOpacity : 0
          cw.targetFill = target.currentFill

          const rgba = parseColorToRgba(target.currentFill)
          cw.targetColorR = rgba[0]
          cw.targetColorG = rgba[1]
          cw.targetColorB = rgba[2]
          cw.targetColorA = rgba[3]

          const isShowingCircle =
            showCircles &&
            target.isHighlighted &&
            target.currentOpacity > 0.01 &&
            circleWords.includes(target.text)
          const baseFontSize = target.size * (showCircles && target.isHighlighted ? 1.35 : 1)
          cw.targetCircleR = isShowingCircle
            ? fadeToCircles
              ? 3.5
              : Math.max(0, baseFontSize * target.text.length * 0.35 + 12)
            : 0
          cw.targetCircleOpacity = isShowingCircle ? 1 : 0

          if (
            cw.lastMode !== mode ||
            cw.lastIndex !== index ||
            Math.abs(cw.targetSize - cw.drawSize) > 0.1
          ) {
            cw.lastMode = mode
            cw.lastIndex = index
            cw.isTransitioning = true
          }
        }
      })
    }

    startRenderLoop()
  })

  function startRenderLoop() {
    if (!rafId) {
      rafId = requestAnimationFrame(renderCanvas)
    }
  }

  function renderCanvas() {
    if (!canvasRef || !width || !height) {
      rafId = null
      return
    }

    const ctx = canvasRef.getContext('2d')

    // Support high DPI displays
    const dpr = window.devicePixelRatio || 1
    canvasRef.width = width * dpr
    canvasRef.height = height * dpr
    ctx.scale(dpr, dpr)

    ctx.clearRect(0, 0, width, height)

    // Move origin to center to match SVG viewBox
    ctx.save()
    ctx.translate(width / 2, height / 2)

    let needsUpdate = false

    canvasWords.forEach((w) => {
      // Color interpolation (always smooth)
      const colorLerp = 0.15
      w.vColorR = (w.targetColorR - w.drawColorR) * colorLerp
      w.vColorG = (w.targetColorG - w.drawColorG) * colorLerp
      w.vColorB = (w.targetColorB - w.drawColorB) * colorLerp
      w.vColorA = (w.targetColorA - w.drawColorA) * colorLerp

      w.drawColorR += w.vColorR
      w.drawColorG += w.vColorG
      w.drawColorB += w.vColorB
      w.drawColorA += w.vColorA

      if (
        Math.abs(w.vColorR) > 0.5 ||
        Math.abs(w.targetColorR - w.drawColorR) > 1 ||
        Math.abs(w.vColorA) > 0.01 ||
        Math.abs(w.targetColorA - w.drawColorA) > 0.05
      ) {
        needsUpdate = true
      }

      // Interpolate values
      if (w.targetX !== undefined) {
        if (w.isTransitioning || isJump) {
          // isJump as fallback for quick index jumps without mode changes
          // Smooth Lerp (Exponential Smoothing) instead of springs to prevent overshoot/wild flights
          const lerpFactor = 0.12

          w.vCenterX = (w.targetCenterX - w.drawCenterX) * lerpFactor
          w.vCenterY = (w.targetCenterY - w.drawCenterY) * lerpFactor
          w.vOffsetX = (w.targetOffsetX - w.drawOffsetX) * lerpFactor
          w.vOffsetY = (w.targetOffsetY - w.drawOffsetY) * lerpFactor

          w.drawCenterX += w.vCenterX
          w.drawCenterY += w.vCenterY
          w.drawOffsetX += w.vOffsetX
          w.drawOffsetY += w.vOffsetY

          w.vSize = (w.targetSize - w.drawSize) * lerpFactor
          w.vTextOpacity = (w.targetTextOpacity - w.drawTextOpacity) * lerpFactor
          w.vRectOpacity = (w.targetRectOpacity - w.drawRectOpacity) * lerpFactor
          w.vCircleR = (w.targetCircleR - w.drawCircleR) * lerpFactor
          w.vCircleOpacity = (w.targetCircleOpacity - w.drawCircleOpacity) * lerpFactor

          w.drawSize += w.vSize
          w.drawTextOpacity += w.vTextOpacity
          w.drawRectOpacity += w.vRectOpacity
          w.drawCircleR += w.vCircleR
          w.drawCircleOpacity += w.vCircleOpacity

          const currentScale = w.size > 0 ? w.drawSize / w.size : 1
          w.drawX = w.drawCenterX + w.drawOffsetX * currentScale
          w.drawY = w.drawCenterY + w.drawOffsetY * currentScale

          let moving = false
          if (Math.abs(w.vCenterX) > 0.1 || Math.abs(w.targetCenterX - w.drawCenterX) > 0.5)
            moving = true
          if (Math.abs(w.vCenterY) > 0.1 || Math.abs(w.targetCenterY - w.drawCenterY) > 0.5)
            moving = true
          if (Math.abs(w.vSize) > 0.1 || Math.abs(w.targetSize - w.drawSize) > 0.5) moving = true
          if (
            Math.abs(w.vTextOpacity) > 0.01 ||
            Math.abs(w.targetTextOpacity - w.drawTextOpacity) > 0.05
          )
            moving = true
          if (
            Math.abs(w.vRectOpacity) > 0.01 ||
            Math.abs(w.targetRectOpacity - w.drawRectOpacity) > 0.05
          )
            moving = true
          if (Math.abs(w.vCircleR) > 0.1 || Math.abs(w.targetCircleR - w.drawCircleR) > 0.5)
            moving = true
          if (
            Math.abs(w.vCircleOpacity) > 0.01 ||
            Math.abs(w.targetCircleOpacity - w.drawCircleOpacity) > 0.05
          )
            moving = true

          if (moving) {
            needsUpdate = true
            w.isTransitioning = true
          } else {
            w.isTransitioning = false
            // Snap cleanly
            w.drawCenterX = w.targetCenterX
            w.drawCenterY = w.targetCenterY
            w.drawOffsetX = w.targetOffsetX
            w.drawOffsetY = w.targetOffsetY
            w.drawX = w.targetX
            w.drawY = w.targetY
            w.drawSize = w.targetSize
            w.drawTextOpacity = w.targetTextOpacity
            w.drawRectOpacity = w.targetRectOpacity
            w.drawCircleR = w.targetCircleR
            w.drawCircleOpacity = w.targetCircleOpacity
            w.vCenterX = 0
            w.vCenterY = 0
            w.vOffsetX = 0
            w.vOffsetY = 0
            w.vSize = 0
            w.vTextOpacity = 0
            w.vRectOpacity = 0
            w.vCircleR = 0
            w.vCircleOpacity = 0
          }
        } else {
          // Snap instantly for scroll offsets
          w.drawCenterX = w.targetCenterX
          w.drawCenterY = w.targetCenterY
          w.drawOffsetX = w.targetOffsetX
          w.drawOffsetY = w.targetOffsetY
          w.drawX = w.targetX
          w.drawY = w.targetY
          w.drawSize = w.targetSize
          w.drawTextOpacity = w.targetTextOpacity
          w.drawRectOpacity = w.targetRectOpacity
          w.drawCircleR = w.targetCircleR
          w.drawCircleOpacity = w.targetCircleOpacity
          w.vCenterX = 0
          w.vCenterY = 0
          w.vOffsetX = 0
          w.vOffsetY = 0
          w.vSize = 0
          w.vTextOpacity = 0
          w.vRectOpacity = 0
          w.vCircleR = 0
          w.vCircleOpacity = 0
        }
      }

      if (w.targetFill) w.currentFill = w.targetFill

      const finalFill = `rgba(${Math.round(w.drawColorR)}, ${Math.round(w.drawColorG)}, ${Math.round(w.drawColorB)}, ${w.drawColorA})`

      // Draw dashed circle
      const isShowingCircle =
        showCircles && w.isHighlighted && w.drawTextOpacity > 0.01 && circleWords.includes(w.text)

      if (w.drawCircleOpacity > 0.01 || w.drawCircleR > 0.5) {
        ctx.globalAlpha = Math.min(1, Math.max(0, w.drawCircleOpacity))
        ctx.beginPath()

        // Shift circle up by ~0.3em so it centers visually on the alphabetic baseline text
        const circleCenterY = w.drawCenterY - w.drawSize * 0.3

        ctx.arc(w.drawCenterX, circleCenterY, Math.max(0, w.drawCircleR), 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(24, 53, 137, 0.8)'
        ctx.fill()

        ctx.lineWidth = 1.5
        ctx.strokeStyle = 'white'
        ctx.setLineDash(fadeToCircles ? [] : [2, 3])
        ctx.stroke()
        ctx.setLineDash([])
      }

      // Draw Rect (Line)
      if (w.drawRectOpacity > 0.01 && w.drawSize > 0.5) {
        ctx.globalAlpha = Math.min(1, Math.max(0, w.drawRectOpacity))
        ctx.fillStyle = finalFill

        const rectWidth = Math.max(0, w.drawSize * w.text.length * 0.45)
        const rectX =
          w.currentTextAnchor === 'middle'
            ? -rectWidth / 2
            : w.currentTextAnchor === 'end'
              ? -rectWidth
              : 0

        ctx.beginPath()
        if (ctx.roundRect) {
          ctx.roundRect(
            w.drawX + rectX,
            w.drawCenterY - w.drawSize * 0.3,
            rectWidth,
            Math.max(2, w.drawSize * 0.15),
            Math.max(1, w.drawSize * 0.075),
          )
        } else {
          ctx.rect(
            w.drawX + rectX,
            w.drawCenterY - w.drawSize * 0.3,
            rectWidth,
            Math.max(2, w.drawSize * 0.15),
          )
        }
        ctx.fill()
      }

      // Draw Text
      if (w.drawTextOpacity > 0.01 && w.drawSize > 0.5) {
        ctx.globalAlpha = Math.min(1, Math.max(0, w.drawTextOpacity))
        ctx.fillStyle = finalFill

        ctx.font = `${Math.max(0, w.drawSize)}px "Selane Twenty", "SelaneTwenty", serif`

        // For circled words, draw exactly at center X to match the circle perfectly horizontally.
        // Keep vertical baseline alphabetic for stability, as Canvas 'middle' can render too low.
        if (isShowingCircle) {
          ctx.textAlign = 'center'
          ctx.textBaseline = 'alphabetic'
          ctx.save()
          ctx.translate(w.drawCenterX, w.drawCenterY)
        } else {
          ctx.textAlign =
            w.currentTextAnchor === 'middle'
              ? 'center'
              : w.currentTextAnchor === 'end'
                ? 'right'
                : 'left'
          ctx.textBaseline = 'alphabetic'
          ctx.save()
          ctx.translate(w.drawX, w.drawY)
        }

        ctx.fillText(w.text, 0, 0)
        ctx.restore()
      }
    })

    if (mode === 'years') {
      ctx.globalAlpha = 1
      ctx.fillStyle = '#ffffff'
      ctx.font = `12px "CuratorRegular", sans-serif`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'

      yearColumns.forEach((col) => {
        const parts = col.label.split(/[-–]/)
        parts.forEach((part, i) => {
          ctx.fillText(part, col.x, col.y + i * 14)
        })
      })
    }

    ctx.restore()

    if (needsUpdate) {
      rafId = requestAnimationFrame(renderCanvas)
    } else {
      rafId = null
    }
  }

  $effect(() => {
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  })
</script>

{#if width > 0 && height > 0}
  <canvas
    bind:this={canvasRef}
    style={`width: ${width}px; height: ${height}px; pointer-events: none;`}
  ></canvas>
{/if}
